import {useEffect, useState} from "react";
import {Alert, Spinner} from "react-bootstrap";
import {Link, useSearchParams} from "react-router";
import {CheckoutApi} from "../../api/CheckoutApi";
import {PageHeader} from "../../components/PageHeader";
import {checkout as messages, rich} from "../../i18n";
import {leavePayPage, loadPaddle} from "./paddle";
import {TrademarkNoticesFor} from "../../components/TrademarkNotices";

// Paying through Paddle (?order=&txn=&sku=, and &key= for a guest's order, from the API's StartCheckout): opens Paddle's checkout over this page, on the
// transaction the API made. The API sends the customer here as a full page load, so CloudFront serves the one
// Content-Security-Policy that allows Paddle.js. Paid, Paddle takes them to /checkout/complete?order=; closed without
// paying, they go back to the product's checkout, as Stripe and PayPal send them. Both leave as full page loads too, so
// Paddle.js doesn't stay loaded on the site's other pages.
export function CheckoutPayPage() {
    const [search] = useSearchParams();
    const orderId = search.get('order') ?? '';
    const transactionId = search.get('txn') ?? '';
    const sku = search.get('sku') ?? '';
    // A guest's order's access key: the return link needs it too.
    const key = search.get('key') ?? '';
    const [error, setError] = useState<unknown>();

    useEffect(() => {
        if (!orderId || !transactionId || !sku) {
            return;
        }
        let current = true;
        let completed = false;
        (async () => {
            const config = await CheckoutApi.config();
            if (!config.paddleCheckout) {
                throw new Error('Paddle isn\'t set up');
            }
            const paddle = await loadPaddle();
            if (!current) {
                return;
            }
            if (config.paddleCheckout.environment === 'sandbox') {
                paddle.Environment.set('sandbox');
            }
            paddle.Initialize({
                token: config.paddleCheckout.clientToken,
                eventCallback: event => {
                    if (event.name === 'checkout.completed') {
                        completed = true;
                    } else if (event.name === 'checkout.error') {
                        console.error('Paddle checkout error', event);
                        if (current) setError(event);
                    } else if (event.name === 'checkout.closed' && !completed) {
                        leavePayPage(`/checkout/${encodeURIComponent(sku)}?cancelled=1`);
                    }
                }
            });
            paddle.Checkout.open({
                transactionId,
                settings: {
                    displayMode: 'overlay',
                    // The transaction's customer (the API set it) is the email, shown locked.
                    successUrl: `${window.location.origin}/checkout/complete?order=${encodeURIComponent(orderId)}${key ? `&key=${encodeURIComponent(key)}` : ''}`,
                    theme: document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light',
                    allowLogout: false
                }
            });
        })().catch(err => {
            console.error('Paddle checkout not opened', err);
            if (current) setError(err);
        });
        return () => {
            current = false;
        };
    }, [orderId, transactionId, sku]);

    const back = (text: string) => <Link to={sku ? `/checkout/${encodeURIComponent(sku)}` : '/products'} reloadDocument>{text}</Link>;
    if (!orderId || !transactionId || !sku || error) {
        return (
            <>
                <PageHeader title={messages.pay.title}/>
                <Alert variant="danger">{rich(messages.pay.failed, { link: back })}</Alert>
            </>
        );
    }
    return (
        <>
            <PageHeader title={messages.pay.title}/>
            <p><Spinner size="sm" className="me-2" aria-hidden="true"/>{messages.pay.opening}</p>
            <p className="small text-body-secondary">{rich(messages.pay.notOpening, { link: back })}</p>
            <TrademarkNoticesFor skus={[sku]} className="mt-5"/>
        </>
    );
}
